import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Minus, Maximize2, Minimize2, GripHorizontal,
  ChevronDown, Brain, Check, Pin,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { ArtifactPanel } from './ArtifactPanel';
import { ResumeBar } from './ResumeBar';
import { SideNav, type SideSection } from './SideNav';
import { ChatMessageStream } from './ChatMessageStream';
import { ChatComposer, type InvokedSkillChip } from './ChatComposer';
import { WorkspaceSidePanel } from './WorkspaceSidePanel';
import { MobileShell } from './MobileShell';
import { useIsMobile } from '../../../hooks/use-media-query';
import { PaFace, type PaFaceState } from '../avatar/PaFace';
import type { PersonalAssistantSectionRequest } from '@/lib/personal-assistant-events';
import type { PendingApproval, RoundEvent, WorkspaceUiArtifact } from './types';
import type { PaToolBadge, PaRagMeta } from '../paStreamReducer';
import type { ArtifactFormSubmission, PaAvatarConfig, PaJob, PaRoutine, WorkspaceState } from '@easyflow/shared';
import type { SlashCommandItem } from './SlashCommandMenu';
import { Z_PA_SHELL, Z_PA_SHELL_DROPDOWN } from '@/constants/zLayers';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: PaToolBadge[];
  // Meta KB del turno: persistita col messaggio e rimappata identica al reload.
  rag?: PaRagMeta;
  created_at: string;
}

interface Conversation {
  id: string;
  title?: string;
  updated_at?: string;
  created_at?: string;
  last_message_at?: string;
  /** Task #3822 — conversazione dentro un Progetto del PA (null = chat libera). */
  project_id?: string | null;
}

export interface WorkspaceAttachedFile {
  id: string;
  file: { name: string };
  type: 'image' | 'document';
  preview?: string;
  url?: string;
  uploading?: boolean;
}

// T008 — opzione modello per lo switcher header (sottoinsieme di LLMModelInfo).
export interface ChatModelOption {
  id: string;
  displayName: string;
  description?: string;
  contextWindow?: number;
  tooltip?: string;
  status?: 'available' | 'deprecated' | 'preview';
  isRecommended?: boolean;
}

export interface WorkspaceShellProps {
  assistantId: string;

  assistantName: string;

  assistantColor: string;

  assistantDescription?: string | null;

  userFirstName?: string | null;

  AssistantIcon: React.ComponentType<{ className?: string }>;

  conversations: Conversation[];

  currentConversationId: string | null;

  onSelectConversation: (id: string) => void;

  onNewConversation: () => void;

  onDeleteConversation: (id: string) => void;

  onBulkDeleteConversations: (ids: string[]) => Promise<void>;

  messages: ChatMessage[];

  streamingContent: string;

  streamingThinking: string;

  activeTools: PaToolBadge[];
  /** Meta KB del turno in streaming (poi persistita e rimappata dallo storico). */

  activeRag?: PaRagMeta;

  isStreaming: boolean;
  /** Task #2435 — messaggio di stato transitorio (rate-limit/retry) emesso dal backend. */

  streamingStatus: string;

  inputValue: string;

  onInputChange: (v: string) => void;

  onSendMessage: () => void;

  // attachments + STT (lifted from PersonalAssistantChat)

  attachedFiles: WorkspaceAttachedFile[];

  onUploadFiles: (files: File[]) => void;

  onRemoveFile: (id: string) => void;

  isRecording: boolean;

  onToggleRecording: () => void;

  workspaceState: WorkspaceState;

  rounds: RoundEvent[];
  /** Vista globale (cross-conversazione) per il pannello Artefatti. */

  artifacts: WorkspaceUiArtifact[];
  /** Artefatti della sola conversazione corrente, per le mini-card inline. */

  conversationArtifacts: WorkspaceUiArtifact[];

  approvals: PendingApproval[];

  onDecideApproval: (id: string, decision: 'approved' | 'rejected', notes?: string, overrideArgs?: Record<string, unknown>) => Promise<void>;

  conversationJobs: PaJob[];

  jobsLoading?: boolean;

  jobsError?: string | null;

  onRetryJobs?: () => Promise<void>;

  onDecideJob: (id: string, decision: 'approved' | 'rejected', note: string | undefined, expectedVersion: number) => Promise<void>;

  onCancelJob: (id: string) => Promise<void>;

  onInterrupt: () => Promise<void>;

  onResume: (instruction?: string) => Promise<void>;

  onArtifactAction?: (artifact: WorkspaceUiArtifact, intent: string) => void;

  onDeleteArtifact?: (id: string) => void | Promise<void>;
  /** Task #3817 — invio dei valori compilati di un artefatto form al PA. */
  onArtifactFormSubmit?: (artifact: WorkspaceUiArtifact, submission: ArtifactFormSubmission) => void;
  /** Task #3817 — rigenera l'ultima risposta assistant (toolbar hover). */
  onRegenerate?: () => void;

  // T008 — switcher modello (opzione B): default agente + chat-model dello stesso
  // provider; lo switch è override per-conversazione, MAI cambio provider.

  modelOptions?: ChatModelOption[];
  /** Modello di default dell'agente (id). */

  defaultModelId?: string | null;
  /** Modello attivo per la conversazione corrente (override o default). */

  currentModelId?: string | null;
  /** true solo se il provider supporta lo switch (openai/claude/gemini). */

  modelSwitchEnabled?: boolean;
  /** Cambio modello: `id` scelto (== default ⇒ azzera l'override). */

  onModelChange?: (modelId: string) => void;

  // Task #3822 — Progetti del PA
  /** Utente corrente: distingue le conversazioni proprie da quelle di altri membri nei progetti. */

  currentUserId?: string | null;
  /** Crea una nuova conversazione dentro il progetto indicato e la apre in chat. */

  onNewProjectConversation?: (projectId: string) => Promise<void> | void;

  onOpenRoutineInChat: (routine?: PaRoutine) => void;

  sectionRequest?: PersonalAssistantSectionRequest;

  onCollapse: () => void;

  onClose: () => void;

  // Task #3041 — avatar personale del PA: quando abilitato subentra all'icona
  // in tutta la superficie (header, messaggi) con layer di stato leggero.

  slashSkills?: SlashCommandItem[];

  invokedSkills?: InvokedSkillChip[];

  onInvokeSkill?: (item: SlashCommandItem) => void;

  onRemoveInvokedSkill?: (id: string) => void;

  avatarEnabled?: boolean;

  avatarConfig?: PaAvatarConfig | null;

  avatarDefaultSeed?: string;

  faceState?: PaFaceState;
  /** Apre il modale di personalizzazione avatar (click sulla faccia in header). */

  onOpenAvatarSettings?: () => void;
}

const DRAWER_STORAGE_KEY = 'pa-ws-drawer-v1';
const PIN_STORAGE_KEY = 'pa-ws-pin-v1';
const DRAWER_WIDTH = 280;
// Larghezza del pannello transitorio (overlay galleggiante o, se pinnato,
// affiancato in-flow): cappata in % così la conversazione resta leggibile.
const PANEL_WIDTH = 'min(620px, 52%)';
// Contenitore della zona conversazione: larghezza piena (niente cap %), così non
// resta una fascia vuota a destra. I gutter orizzontali (~1cm per lato) sono
// applicati come `px-[1cm]` sui wrapper di messaggi/chip/barra input, che così
// condividono la stessa colonna allineata e sfruttano quasi tutta la larghezza
// interna del contenitore (si riadatta da solo all'apertura del pannello artefatti).
const CONV_COL = 'w-full';
// Blocco testo dell'assistente: ancorato a sinistra, niente bolla pesante, a
// piena larghezza del contenitore; il cap della misura di lettura (~70ch) è
// applicato sul wrapper `prose` interno, così timestamp/meta restano full-width.
const ASSISTANT_BLOCK = 'w-full';

// Contenuto del pannello transitorio di destra: SOLO il contenuto di un
// artefatto (documento aperto di fianco alla chat). Conferme e Memoria vivono
// nel pannello laterale unico (drawer), come tutte le altre sezioni.
type OverlayContent = 'artifacts';

function loadDrawerOpen(): boolean {
  try {
    const raw = window.localStorage.getItem(DRAWER_STORAGE_KEY);
    return raw === '1';
  } catch { return false; }
}

function loadPinned(): boolean {
  try {
    const raw = window.localStorage.getItem(PIN_STORAGE_KEY);
    return raw === '1';
  } catch { return false; }
}

export const WorkspaceShell: React.FC<WorkspaceShellProps> = (props) => {
  const {
    assistantId, assistantName, assistantColor, assistantDescription, userFirstName, AssistantIcon,
    conversations, currentConversationId, onSelectConversation, onNewConversation, onDeleteConversation, onBulkDeleteConversations,
    messages, streamingContent, streamingThinking, activeTools, activeRag, isStreaming, streamingStatus, inputValue, onInputChange, onSendMessage,
    attachedFiles, onUploadFiles, onRemoveFile, isRecording, onToggleRecording,
    workspaceState, rounds, artifacts, conversationArtifacts, approvals, onDecideApproval, onInterrupt, onResume, onArtifactAction, onDeleteArtifact,
    onArtifactFormSubmit, onRegenerate,
    conversationJobs, jobsLoading = false, jobsError = null, onRetryJobs, onDecideJob, onCancelJob,
    modelOptions = [], defaultModelId = null, currentModelId = null, modelSwitchEnabled = false, onModelChange,
    currentUserId = null, onNewProjectConversation,
    onOpenRoutineInChat, sectionRequest,
    slashSkills, invokedSkills, onInvokeSkill, onRemoveInvokedSkill,
    onCollapse, onClose,
    avatarEnabled = false, avatarConfig = null, avatarDefaultSeed = 'pa', faceState = 'idle', onOpenAvatarSettings,
  } = props;

  const [maximized, setMaximized] = useState(false);
  const [section, setSection] = useState<SideSection>('chat');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => loadDrawerOpen());
  // Pannello transitorio di destra: `null` = chiuso. `overlayPinned` decide se
  // galleggia sopra la chat (transitorio) o resta affiancato in-flow.
  const [overlayContent, setOverlayContent] = useState<OverlayContent | null>(null);
  const [overlayPinned, setOverlayPinned] = useState<boolean>(() => loadPinned());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  // Apertura/chiusura del pannello transitorio. L'overlay galleggia sopra la
  // chat (che resta a larghezza piena) finché non è pinnato.
  const closeOverlay = useCallback(() => setOverlayContent(null), []);
  const toggleArtifacts = useCallback(() => {
    setOverlayContent(prev => (prev === 'artifacts' ? null : 'artifacts'));
  }, []);
  const togglePin = useCallback(() => setOverlayPinned(p => !p), []);

  const toggleMaximize = useCallback(() => setMaximized(m => !m), []);

  const openArtifact = useCallback((id: string) => {
    setSelectedId(id);
    setOverlayContent('artifacts');
  }, []);

  // ESC two-step: prima chiude drawer/pannello aperti, poi (seconda pressione)
  // riduce a FAB. ESC è un dismiss esplicito: chiude il pannello anche se
  // pinnato. Permette di consultare la conversazione full-width prima di
  // minimizzare.
  useEffect(() => {
    if (isMobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (
        section === 'routines'
        && document.querySelector('[role="dialog"], [role="alertdialog"], [role="listbox"]')
      ) return;
      e.preventDefault();
      if (drawerOpen || overlayContent) {
        if (drawerOpen) setDrawerOpen(false);
        if (overlayContent) setOverlayContent(null);
        return;
      }
      onCollapse();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCollapse, drawerOpen, overlayContent, isMobile, section]);

  // Auto-apri il pannello artefatti + seleziona l'artefatto fresco. In stato
  // non pinnato entra come overlay transitorio sopra la conversazione.
  useEffect(() => {
    const fresh = artifacts.find(a => a.isFresh);
    if (fresh) {
      setSelectedId(fresh.id);
      setOverlayContent('artifacts');
    }
  }, [artifacts]);

  // Le richieste di approvazione restano renderizzate inline nella chat (vedi
  // blocco ToolApprovalCard sotto): non forziamo più l'apertura del pannello
  // "approvals", che strappava il contesto all'utente mentre leggeva la
  // conversazione. Il rail SideNav mostra comunque il contatore.

  useEffect(() => {
    try { window.localStorage.setItem(DRAWER_STORAGE_KEY, drawerOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [drawerOpen]);
  useEffect(() => {
    try { window.localStorage.setItem(PIN_STORAGE_KEY, overlayPinned ? '1' : '0'); } catch { /* ignore */ }
  }, [overlayPinned]);

  // Routing del rail: TUTTE le sezioni (chat/attività/artefatti/conferme/
  // memoria) navigano nell'unico pannello laterale (drawer). Il pannello
  // transitorio di destra resta riservato al contenuto di un artefatto aperto.
  const handleSectionToggle = useCallback((s: SideSection) => {
    setSection(prev => {
      if (prev === s) {
        setDrawerOpen(d => !d);
        return prev;
      }
      setDrawerOpen(true);
      return s;
    });
  }, []);

  useEffect(() => {
    if (!sectionRequest) return;
    setOverlayContent(null);
    setSection(sectionRequest.section);
    setDrawerOpen(true);
  }, [sectionRequest]);

  const handleOpenRoutineInChat = useCallback((routine?: PaRoutine) => {
    setDrawerOpen(false);
    onOpenRoutineInChat(routine);
    window.setTimeout(() => {
      shellRef.current
        ?.querySelector<HTMLTextAreaElement>('textarea[placeholder="Scrivi un messaggio…"]')
        ?.focus();
    }, 0);
  }, [onOpenRoutineInChat]);

  // Titolo della conversazione attiva (aggiornato live: l'array conversations
  // viene patchato in-place sull'evento conversation_titled).
  const currentConversationTitle = useMemo(() => {
    if (!currentConversationId) return null;
    const c = conversations.find(cv => cv.id === currentConversationId);
    const t = c?.title?.trim();
    if (!t || t === 'New Conversation' || t === 'Nuova conversazione') return null;
    return t;
  }, [conversations, currentConversationId]);

  // T008 — etichetta del modello attivo (senza prefisso provider) per il trigger.
  const currentModelLabel = useMemo(() => {
    const found = modelOptions.find(m => m.id === currentModelId);
    if (found) return found.displayName;
    return currentModelId || 'Modello';
  }, [modelOptions, currentModelId]);

  // Eliminazione artefatto dal pannello laterale: se l'artefatto eliminato è
  // quello aperto nel pannello transitorio, lo deseleziona (chiude il viewer).
  const handleDeleteArtifactFromPanel = useCallback((id: string) => {
    if (selectedId === id) setSelectedId(null);
    return onDeleteArtifact?.(id);
  }, [selectedId, onDeleteArtifact]);

  // Drag&drop allegati sul pannello chat (Task #1445)
  const [isDragging, setIsDragging] = useState(false);
  const dragDepthRef = useRef(0);
  const handleDragEnter = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (isStreaming || isRecording) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) onUploadFiles(files);
  };

  // Modal margins: finestra ~20% più piccola (margini esterni più ampi),
  // inset verticale/orizzontale distinti così la riduzione orizzontale non
  // schiaccia l'altezza utile. Maximized = fullscreen.
  const shellStyle: React.CSSProperties = maximized
    ? { inset: 0, borderRadius: 0, zIndex: Z_PA_SHELL }
    : { inset: 'clamp(24px, 14vh, 200px) clamp(32px, 14vw, 380px)', zIndex: Z_PA_SHELL };

  // Vista mobile isolata (≤767px): frame full-screen dedicato che consuma gli
  // stessi sotto-componenti presentazionali condivisi (single source of truth).
  // Il branch è DOPO tutti gli hook (regole React) e PRIMA del return desktop.
  // Entrambi i return sono portalati su `document.body` per sganciare lo shell
  // da wrapper con `overflow`/transform che ne clipperebbero il `fixed`.
  if (isMobile) return createPortal(<MobileShell {...props} />, document.body);

  return createPortal(
    <div
      ref={shellRef}
      className="fixed bg-card shadow-2xl border border-border flex flex-col overflow-hidden"
      style={{ ...shellStyle, borderRadius: maximized ? 0 : 12 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card shrink-0 cursor-default select-none">
        <div className="flex items-center gap-2 min-w-0">
          <GripHorizontal className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {/* Task #3041 — faccia del PA in header: icona (OFF) o avatar con
              stato; click = personalizzazione avatar/nome. */}
          <button
            type="button"
            onClick={onOpenAvatarSettings}
            title="Avatar assistente — personalizza"
            className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <PaFace
              avatarEnabled={avatarEnabled}
              avatarConfig={avatarConfig}
              defaultSeed={avatarDefaultSeed}
              color={assistantColor}
              FallbackIcon={AssistantIcon}
              state={faceState}
              size={28}
            />
          </button>
          <div className="leading-tight min-w-0 flex-1">
            <div className="font-semibold text-sm text-foreground truncate">{assistantName}</div>
            <div
              className="text-[10px] text-muted-foreground truncate"
              title={currentConversationTitle ?? undefined}
            >
              {currentConversationTitle ?? 'Workspace AI'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {modelSwitchEnabled && modelOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 max-w-[170px] gap-1 px-2 text-[11px] font-medium text-foreground"
                  title="Modello per questa conversazione"
                >
                  <Brain className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{currentModelLabel}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: Z_PA_SHELL_DROPDOWN }} className="w-72 max-h-[60vh] overflow-y-auto">
                <DropdownMenuLabel className="text-[11px] text-muted-foreground">
                  Modello · questa conversazione
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {modelOptions.map((m) => {
                  const active = m.id === currentModelId;
                  const isDefault = m.id === defaultModelId;
                  const hint = m.description?.trim()
                    ? m.description
                    : (m.contextWindow ? `Contesto ~${Math.round(m.contextWindow / 1000)}k token` : '');
                  return (
                    <DropdownMenuItem
                      key={m.id}
                      onSelect={() => { if (!active) onModelChange?.(m.id); }}
                      title={m.tooltip || m.description || undefined}
                      className="flex flex-col items-start gap-0.5 py-1.5"
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="flex-1 truncate text-[12px] font-medium text-foreground">{m.displayName}</span>
                        {isDefault && (
                          <span className="rounded bg-muted px-1 text-[9px] uppercase tracking-wide text-muted-foreground">default</span>
                        )}
                        {active && <Check className="h-3.5 w-3.5 shrink-0 text-violet-600" />}
                      </div>
                      {hint && <span className="line-clamp-2 text-[10px] text-muted-foreground">{hint}</span>}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onCollapse} title="Minimizza (ESC)">
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={toggleMaximize}
            title={maximized ? 'Riduci' : 'Schermo intero'}
          >
            {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose} title="Chiudi">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Rail SideNav */}
        <SideNav
          active={section}
          onChange={handleSectionToggle}
          approvalsCount={approvals.length}
          artifactsCount={artifacts.length}
        />

        {/* Drawer (toggleable, default closed) */}
        {drawerOpen && (
          <div
            className="shrink-0 border-r border-border bg-muted min-h-0"
            style={{ width: DRAWER_WIDTH }}
          >
            <WorkspaceSidePanel
              section={section}
              conversations={conversations}
              currentConversationId={currentConversationId}
              onSelectConversation={onSelectConversation}
              onNewConversation={onNewConversation}
              onDeleteConversation={onDeleteConversation}
              onBulkDeleteConversations={onBulkDeleteConversations}
              rounds={rounds}
              artifacts={artifacts}
              selectedArtifactId={selectedId}
              onSelectArtifact={openArtifact}
              onDeleteArtifact={onDeleteArtifact ? handleDeleteArtifactFromPanel : undefined}
              onToggleArtifactPanel={toggleArtifacts}
              artifactPanelOpen={overlayContent === 'artifacts'}
              approvals={approvals}
              onDecideApproval={onDecideApproval}
              onOpenRoutineInChat={handleOpenRoutineInChat}
              assistantId={assistantId}
              currentUserId={currentUserId}
              onNewProjectConversation={onNewProjectConversation}
            />
          </div>
        )}

        {/* Chat full-width + pannello transitorio (overlay galleggiante o,
            se pinnato, affiancato in-flow) ancorato a destra */}
        <div className="flex-1 flex min-w-0 min-h-0 relative">
          {/* Chat panel: sempre a larghezza piena, mai ristretto dall'overlay */}
          <div
            className="flex-1 flex flex-col min-w-0 min-h-0 bg-card relative"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center bg-violet-500/10 border-2 border-dashed border-violet-400 rounded">
                <div className="text-violet-700 text-sm font-medium">Rilascia per allegare</div>
              </div>
            )}
            <ScrollArea className="flex-1">
              <ChatMessageStream
                assistantName={assistantName}
                assistantColor={assistantColor}
                assistantDescription={assistantDescription}
                userFirstName={userFirstName}
                AssistantIcon={AssistantIcon}
                messages={messages}
                streamingContent={streamingContent}
                streamingThinking={streamingThinking}
                activeTools={activeTools}
                activeRag={activeRag}
                isStreaming={isStreaming}
                streamingStatus={streamingStatus}
                conversationArtifacts={conversationArtifacts}
                approvals={approvals}
                onDecideApproval={onDecideApproval}
                conversationJobs={conversationJobs}
                jobsLoading={jobsLoading}
                jobsError={jobsError}
                onRetryJobs={onRetryJobs}
                onDecideJob={onDecideJob}
                onCancelJob={onCancelJob}
                onOpenArtifact={openArtifact}
                onRegenerate={onRegenerate}
                avatarEnabled={avatarEnabled}
                avatarConfig={avatarConfig}
                avatarDefaultSeed={avatarDefaultSeed}
                faceState={faceState}
              />
            </ScrollArea>

            <ResumeBar state={workspaceState} onResume={onResume} />

            <ChatComposer
              inputValue={inputValue}
              onInputChange={onInputChange}
              onSendMessage={onSendMessage}
              onInterrupt={onInterrupt}
              isStreaming={isStreaming}
              isRecording={isRecording}
              onToggleRecording={onToggleRecording}
              attachedFiles={attachedFiles}
              onUploadFiles={onUploadFiles}
              onRemoveFile={onRemoveFile}
              currentConversationId={currentConversationId}
              assistantId={assistantId}
              slashSkills={slashSkills}
              invokedSkills={invokedSkills}
              onInvokeSkill={onInvokeSkill}
              onRemoveInvokedSkill={onRemoveInvokedSkill}
            />
          </div>

          {/* Backdrop trasparente: dismiss su click-fuori. Solo overlay
              transitorio (non pinnato): il pannello pinnato resta in-flow. */}
          {overlayContent && !overlayPinned && (
            <div
              className="absolute inset-0 z-30 bg-transparent"
              onClick={closeOverlay}
              aria-hidden="true"
            />
          )}

          {/* Pannello transitorio: SOLO contenuto di un artefatto aperto.
              Pinnato: affiancato in-flow (shrink-0, no auto-dismiss). Non
              pinnato: overlay assoluto che scivola da destra sopra la chat. */}
          {overlayContent && (
            <div
              className={
                overlayPinned
                  ? 'shrink-0 min-h-0 flex flex-col border-l border-border bg-card'
                  : 'absolute inset-y-0 right-0 z-40 min-h-0 flex flex-col border-l border-border bg-card shadow-2xl'
              }
              style={{ width: PANEL_WIDTH }}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
                <span className="text-[11px] font-semibold uppercase text-muted-foreground">
                  Artefatti
                </span>
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 w-7 p-0 ${overlayPinned ? 'text-violet-700 bg-violet-50' : 'text-muted-foreground'}`}
                    onClick={togglePin}
                    title={overlayPinned ? 'Sblocca pannello (torna overlay)' : 'Fissa pannello affiancato'}
                  >
                    <Pin className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground"
                    onClick={closeOverlay}
                    title="Chiudi (ESC)"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                {overlayContent === 'artifacts' && (() => {
                  const sorted = [...artifacts].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
                  const active = sorted.find(a => a.id === selectedId) ?? sorted[0] ?? null;
                  if (!active) return (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground px-4 text-center bg-muted">
                      Nessun artefatto da mostrare.
                    </div>
                  );
                  // Versioni sorelle (stessa conversazione+slug) per il
                  // selettore versioni nell'header del pannello (Task #3817).
                  const versions = artifacts.filter(a =>
                    a.slug === active.slug && a.conversationId === active.conversationId);
                  return (
                    <ArtifactPanel
                      artifact={active}
                      onAction={onArtifactAction}
                      onFormSubmit={onArtifactFormSubmit}
                      versions={versions}
                      onSelectVersion={setSelectedId}
                    />
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
